/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Session Monitor Hook - Activity Tracking & Auto-Logout
 * ============================================================================
 *
 * Monitors user activity and enforces session timeout policies.
 * Features:
 * - Tracks user activity (clicks, keypress, mouse movement, scroll)
 * - Updates lastActivity timestamp in auth store
 * - Periodically checks session validity
 * - Auto-logs out when session expires
 * - Shows warning before session expires
 * - Refreshes JWT token when user extends session
 *
 * ============================================================================
 */

import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, setSessionInactivityMinutes, getSessionInactivityMs } from '@/stores/authStore';
import { useMyOrgSettings } from '@/hooks/api/useMyOrgSettings';
import { projectConfig } from '@/config/project.config';
import { authService } from '@/services/auth.service';
import { getJwtExpiry } from '@/lib/utils';

interface SessionMonitorOptions {
  /** How often to check session validity (ms). Default: 30000 (30 seconds) */
  checkInterval?: number;
  /** Show warning this many minutes before expiry. Default: 2 minutes */
  warningMinutes?: number;
  /** Events to track as user activity */
  activityEvents?: string[];
  /** Throttle activity updates (ms). Default: 60000 (1 minute) */
  activityThrottle?: number;
}

interface SessionMonitorReturn {
  /** Whether the session warning modal should be shown */
  showWarning: boolean;
  /** Minutes remaining before session expires */
  minutesRemaining: number;
  /** Seconds remaining (for more precise countdown) */
  secondsRemaining: number;
  /** Whether expiry is due to token (vs inactivity) */
  isTokenExpiry: boolean;
  /** Whether session extension is in progress */
  isExtending: boolean;
  /** Extend the session (refresh token if needed, reset activity timer) */
  extendSession: () => void;
  /** Dismiss the warning without extending */
  dismissWarning: () => void;
  /** Force logout now */
  logoutNow: () => void;
}

const DEFAULT_OPTIONS: Required<SessionMonitorOptions> = {
  checkInterval: 1000, // 1 second for accurate countdown when warning is shown
  warningMinutes: 5, // Show warning 5 minutes before JWT expiry
  activityEvents: ['mousedown', 'keydown', 'scroll', 'touchstart'],
  activityThrottle: 60 * 1000, // 1 minute
};

/**
 * Hook to monitor session and enforce timeout policies
 */
export function useSessionMonitor(options: SessionMonitorOptions = {}): SessionMonitorReturn {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    accessToken,
    refreshToken: storedRefreshToken,
    lastActivity,
    updateLastActivity,
    setTokens,
    logout,
    isSessionValid,
  } = useAuthStore();

  const opts = { ...DEFAULT_OPTIONS, ...options };
  const lastActivityUpdateRef = useRef<number>(0);
  const warningDismissedRef = useRef<boolean>(false);
  /** Guards against firing several silent refreshes from the 1s check interval. */
  const silentRefreshRef = useRef<boolean>(false);

  const [showWarning, setShowWarning] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(0);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [isTokenExpiry, setIsTokenExpiry] = useState(false);
  const [isExtending, setIsExtending] = useState(false);

  // Organisations can shorten or extend the auto-logout via the
  // `session_timeout_minutes` org setting; the project default applies until it
  // loads, and for orgs that never override it.
  const { data: orgSettings } = useMyOrgSettings();
  const orgInactivityMinutes = useMemo(() => {
    const raw = orgSettings?.settings.find(s => s.key === 'session_timeout_minutes')?.effectiveValue;
    const parsed = raw != null ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [orgSettings]);

  // Keep the store (which cannot use hooks) in step with the resolved value.
  useEffect(() => {
    setSessionInactivityMinutes(orgInactivityMinutes);
  }, [orgInactivityMinutes]);

  const inactivityTimeoutMs = orgInactivityMinutes
    ? orgInactivityMinutes * 60 * 1000
    : getSessionInactivityMs();

  /**
   * Handle user activity - update lastActivity with throttling
   */
  const handleActivity = useCallback(() => {
    if (!isAuthenticated) return;

    const now = Date.now();
    // Throttle updates to avoid excessive store updates
    if (now - lastActivityUpdateRef.current >= opts.activityThrottle) {
      updateLastActivity();
      lastActivityUpdateRef.current = now;
      // Reset warning dismissed state on activity
      warningDismissedRef.current = false;
    }
  }, [isAuthenticated, updateLastActivity, opts.activityThrottle]);

  /**
   * Extend session - refresh token if JWT is expiring, reset activity timer
   */
  const extendSession = useCallback(async () => {
    setIsExtending(true);

    // IMMEDIATELY update activity and dismiss warning to prevent race conditions
    // with the checkSession interval that runs every second
    updateLastActivity();
    lastActivityUpdateRef.current = Date.now();
    setShowWarning(false);
    warningDismissedRef.current = false;

    try {
      // Check if we should refresh the token
      const jwtExpiresAt = getJwtExpiry(accessToken);
      const now = Date.now();
      const tokenExpiresInMs = jwtExpiresAt ? jwtExpiresAt - now : Infinity;
      const warningThresholdMs = opts.warningMinutes * 60 * 1000;

      // Refresh token if:
      // 1. Token is expiring within the warning threshold, OR
      // 2. The warning was shown due to token expiry (isTokenExpiry is true)
      const shouldRefreshToken = (tokenExpiresInMs <= warningThresholdMs || isTokenExpiry) && storedRefreshToken;

      if (shouldRefreshToken) {
        console.debug('[Session] Refreshing token...');
        try {
          const tokenPair = await authService.refreshToken({ refreshToken: storedRefreshToken });

          if (tokenPair?.accessToken && tokenPair?.refreshToken) {
            setTokens(tokenPair.accessToken, tokenPair.refreshToken);
            console.debug('[Session] Token refreshed successfully');
          } else {
            console.error('[Session] Invalid token pair received:', tokenPair);
            // Don't throw - the activity was already updated, so inactivity is reset
            // The token will just continue until it expires
          }
        } catch (refreshError) {
          console.error('[Session] Token refresh failed:', refreshError);
          // If the warning was for token expiry and refresh failed, we should logout
          if (isTokenExpiry) {
            logout();
            navigate('/auth/login', {
              replace: true,
              state: { message: 'Session expired. Please log in again.' }
            });
            return;
          }
          // If it was inactivity warning, activity is already reset - we can continue
          console.debug('[Session] Continuing despite refresh failure (inactivity reset)');
        }
      }

      setIsTokenExpiry(false);
    } catch (error) {
      console.error('[Session] Unexpected error in extendSession:', error);
      // Only logout for truly unexpected errors, not refresh failures
    } finally {
      setIsExtending(false);
    }
  }, [accessToken, storedRefreshToken, opts.warningMinutes, isTokenExpiry, updateLastActivity, setTokens, logout, navigate]);

  /**
   * Dismiss warning without extending session
   */
  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    warningDismissedRef.current = true;
  }, []);

  /**
   * Force logout now
   */
  const logoutNow = useCallback(() => {
    logout();
    navigate('/auth/login', {
      replace: true,
      state: { message: 'You have been logged out.' }
    });
  }, [logout, navigate]);

  /**
   * Check session validity and handle expiry
   * Primarily based on JWT token expiry from the backend
   */
  const checkSession = useCallback(() => {
    if (!isAuthenticated) return;

    // Check if session is still valid
    if (!isSessionValid()) {
      logout();
      navigate('/auth/login', {
        replace: true,
        state: { message: 'Your session has expired. Please log in again.' }
      });
      return;
    }

    const now = Date.now();

    // Get JWT token expiry directly from the token - this is the primary source of truth
    const jwtExpiresAt = getJwtExpiry(accessToken);

    if (!jwtExpiresAt) {
      // If we can't read the token expiry, something is wrong
      console.warn('Could not read JWT expiry time');
      return;
    }

    // Calculate inactivity expiry as secondary check
    const inactivityExpiresAt = (lastActivity || now) + inactivityTimeoutMs;

    // Determine which expires first
    const tokenExpiresSooner = jwtExpiresAt < inactivityExpiresAt;
    const expiresAt = Math.min(inactivityExpiresAt, jwtExpiresAt);
    const msRemaining = Math.max(0, expiresAt - now);

    // Calculate minutes and seconds for precise countdown
    const totalSeconds = Math.floor(msRemaining / 1000);
    const minsRemaining = Math.floor(totalSeconds / 60);
    const secsRemaining = totalSeconds % 60;

    setMinutesRemaining(minsRemaining);
    setSecondsRemaining(secsRemaining);
    setIsTokenExpiry(tokenExpiresSooner);

    // Auto-logout when time runs out
    if (msRemaining <= 0) {
      logout();
      navigate('/auth/login', {
        replace: true,
        state: { message: 'Your session has expired. Please log in again.' }
      });
      return;
    }

    const warningThresholdMs = opts.warningMinutes * 60 * 1000;

    // An expiring ACCESS TOKEN is routine housekeeping, not a reason to
    // interrupt someone mid-task: the refresh token is valid for days. Renew it
    // in the background and say nothing. Prompting here is what made a 15-minute
    // token rotation look like a session timeout, and losing unsaved work to it
    // is pure waste.
    if (tokenExpiresSooner && storedRefreshToken && msRemaining <= warningThresholdMs) {
      if (!silentRefreshRef.current) {
        silentRefreshRef.current = true;
        authService.refreshToken({ refreshToken: storedRefreshToken })
          .then((pair) => {
            if (pair?.accessToken && pair?.refreshToken) {
              setTokens(pair.accessToken, pair.refreshToken);
            }
          })
          .catch(() => {
            // Only a genuinely dead refresh token reaches the user, and then as
            // the real warning rather than a silent failure.
            setShowWarning(true);
          })
          .finally(() => { silentRefreshRef.current = false; });
      }
      setShowWarning(false);
      return;
    }

    // Show warning if within warning threshold and not dismissed
    if (msRemaining <= warningThresholdMs && msRemaining > 0 && !warningDismissedRef.current) {
      setShowWarning(true);
    } else if (msRemaining > warningThresholdMs) {
      setShowWarning(false);
    }
  }, [
    isAuthenticated,
    isSessionValid,
    accessToken,
    lastActivity,
    inactivityTimeoutMs,
    opts.warningMinutes,
    // Needed by the silent refresh above; without them this closure would keep
    // refreshing with the token it captured on mount.
    storedRefreshToken,
    setTokens,
    logout,
    navigate,
  ]);

  /**
   * Set up activity listeners
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    // Add activity event listeners
    opts.activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initialize last activity if not set
    if (!lastActivity) {
      updateLastActivity();
    }

    return () => {
      opts.activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, handleActivity, opts.activityEvents, lastActivity, updateLastActivity]);

  /**
   * Set up periodic session check
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    // Initial check
    checkSession();

    // Set up interval
    const intervalId = setInterval(checkSession, opts.checkInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, checkSession, opts.checkInterval]);

  return {
    showWarning,
    minutesRemaining,
    secondsRemaining,
    isTokenExpiry,
    isExtending,
    extendSession,
    dismissWarning,
    logoutNow,
  };
}

export default useSessionMonitor;
